import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, formatPrice } from "@/lib/api";
import { STATUS_LABEL, useCustomer } from "@/context/CustomerContext";
import { AddressFields, shopBtn, shopInput } from "@/components/shop/AddressFields";

const AuthForms = () => {
  const { login, register } = useCustomer();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", first_name: "", last_name: "", privacy_ack: false });
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setError("");
    try { mode === "login" ? await login(f.email.trim().toLowerCase(), f.password) : await register({ ...f, email: f.email.trim().toLowerCase() }); } catch (err) { setError(apiError(err)); }
  };
  return (
    <form onSubmit={submit} className="max-w-md space-y-3" data-testid={`account-${mode}-form`}>
      <div className="flex gap-6 mb-6 text-xs uppercase tracking-[0.2em]">
        {[["login", "Accedi"], ["register", "Crea account"]].map(([m, l]) => (
          <button type="button" key={m} data-testid={`account-tab-${m}`} onClick={() => setMode(m)} className={`pb-1 border-b ${mode === m ? "border-[#956842] text-[#39251D]" : "border-transparent text-[#242321]/50"}`}>{l}</button>
        ))}
      </div>
      {mode === "register" && <div className="grid grid-cols-2 gap-3">
        <input data-testid="account-first-name" required placeholder="Nome" value={f.first_name} onChange={set("first_name")} className={shopInput} />
        <input data-testid="account-last-name" required placeholder="Cognome" value={f.last_name} onChange={set("last_name")} className={shopInput} />
      </div>}
      <input data-testid="account-email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} required placeholder="Email" value={f.email} onChange={set("email")} className={shopInput} />
      <input data-testid="account-password" type="password" autoCapitalize="none" autoCorrect="off" spellCheck={false} required minLength={mode === "register" ? 8 : 1} placeholder={mode === "register" ? "Password (min. 8 caratteri)" : "Password"} value={f.password} onChange={set("password")} className={shopInput} />
      {mode === "register" && <label className="flex gap-2 text-xs"><input data-testid="account-privacy" type="checkbox" required checked={f.privacy_ack} onChange={set("privacy_ack")} />Ho preso visione della <Link to="/privacy" target="_blank" className="underline">Privacy Policy</Link></label>}
      {error && <p data-testid="account-error" className="text-sm text-[#8a2f22]">{error}</p>}
      <button data-testid="account-submit" className={`${shopBtn} w-full`}>{mode === "login" ? "Accedi" : "Crea account"}</button>
      {mode === "login" && <Link to="/account/recupera-password" data-testid="account-forgot-link" className="block text-xs underline">Password dimenticata?</Link>}
      <p className="text-xs text-[#242321]/60 pt-2">Puoi sempre acquistare anche come ospite, senza registrarti.</p>
      <p data-testid="account-admin-hint" className="text-xs text-[#242321]/70 border-t border-[#D5B98C]/50 pt-3">Questa pagina è per i clienti del negozio. Se sei il titolare o un collaboratore, accedi dall'<Link to="/admin/login" data-testid="account-admin-login-link" className="underline font-medium">Area riservata (/admin/login)</Link>.</p>
    </form>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/customer/orders").then((r) => setOrders(r.data)); }, []);
  if (!orders) return null;
  if (!orders.length) return <p className="text-sm text-[#242321]/60" data-testid="account-no-orders">Non hai ancora effettuato ordini.</p>;
  return (
    <ul className="space-y-2" data-testid="account-orders">
      {orders.map((o) => (
        <li key={o.id} data-testid={`account-order-${o.number}`} className="flex flex-wrap justify-between gap-2 p-4 bg-white/60 border border-[#D5B98C]/50 text-sm">
          <span><b>{o.number}</b> · {new Date(o.created_at).toLocaleDateString("it-IT")} · {o.items.length} articoli</span>
          <span>{formatPrice(o.total)} · <span className="text-[#956842]">{STATUS_LABEL[o.status]}</span>{o.tracking?.code && ` · BRT ${o.tracking.code}`}</span>
        </li>
      ))}
    </ul>
  );
};

const Addresses = ({ customer, setCustomer }) => {
  const [list, setList] = useState(customer.addresses || []);
  const [countries, setCountries] = useState([]);
  useEffect(() => { api.get("/shop/config").then((r) => setCountries(r.data.countries)); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { setCustomer((await api.put("/customer/addresses", list)).data); toast.success("Indirizzi salvati"); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <form onSubmit={save} className="space-y-5 max-w-xl" data-testid="account-addresses">
      {list.map((a, i) => (
        <div key={a.id || i} className="p-4 border border-[#D5B98C]/60 bg-white/50">
          <AddressFields value={a} onChange={(v) => setList(list.map((x, k) => (k === i ? v : x)))} countries={countries} prefix={`address-${i}`} />
          <button type="button" data-testid={`address-remove-${i}`} onClick={() => setList(list.filter((_, k) => k !== i))} className="mt-2 text-xs underline">Rimuovi</button>
        </div>
      ))}
      <div className="flex gap-3">
        <button type="button" data-testid="address-add" onClick={() => setList([...list, { country: "IT" }])} className="text-xs uppercase tracking-widest border-b border-[#956842]">Aggiungi indirizzo</button>
        <button data-testid="address-save" className={shopBtn}>Salva</button>
      </div>
    </form>
  );
};

const Profile = ({ customer, setCustomer, onDeleted }) => {
  const [f, setF] = useState({ first_name: customer.first_name, last_name: customer.last_name, phone: customer.phone || "" });
  const [pwd, setPwd] = useState("");
  const save = async (e) => { e.preventDefault(); try { setCustomer((await api.patch("/customer/me", f)).data); toast.success("Dati aggiornati"); } catch (err) { toast.error(apiError(err)); } };
  const del = async () => {
    if (!window.confirm("Eliminare definitivamente il tuo account? I dati degli ordini restano conservati solo per gli obblighi fiscali.")) return;
    try { await api.post("/customer/delete-account", { password: pwd }); toast.success("Account eliminato"); onDeleted(); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="max-w-md space-y-10">
      <form onSubmit={save} className="space-y-3" data-testid="account-profile">
        <input data-testid="profile-first-name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className={shopInput} />
        <input data-testid="profile-last-name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className={shopInput} />
        <input data-testid="profile-phone" placeholder="Telefono" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={shopInput} />
        <p className="text-xs text-[#242321]/60">Email: {customer.email}</p>
        <button data-testid="profile-save" className={shopBtn}>Salva</button>
      </form>
      <div className="p-5 border border-[#8a2f22]/30 bg-[#8a2f22]/5" data-testid="account-delete">
        <div className="font-serif text-xl text-[#8a2f22]">Elimina il mio account</div>
        <p className="text-xs mt-2 text-[#242321]/70">Per confermare la tua identità inserisci la password. Nome, email, telefono e indirizzi saranno cancellati; gli ordini restano conservati, scollegati dall'account, solo per gli obblighi fiscali e contabili.</p>
        <input data-testid="delete-password" type="password" placeholder="Password" value={pwd} onChange={(e) => setPwd(e.target.value)} className={`${shopInput} mt-3`} />
        <button data-testid="delete-account-button" disabled={!pwd} onClick={del} className="mt-3 w-full py-3 text-xs uppercase tracking-[0.2em] bg-[#8a2f22] text-white disabled:opacity-40">Elimina il mio account</button>
      </div>
    </div>
  );
};

export default function AccountPage() {
  const { customer, setCustomer, logout } = useCustomer();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("ordini");
  if (customer === null) return <div className="min-h-[50vh]" />;
  if (customer && sp.get("next")) return <Navigate to={sp.get("next")} replace />;
  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-14" data-testid="account-page">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#956842] mb-3">Area clienti</div>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#39251D] mb-10">{customer ? `Ciao, ${customer.first_name}` : "Il tuo account"}</h1>
      {!customer ? <AuthForms /> : (
        <>
          <div className="flex flex-wrap gap-2 mb-8">
            {[["ordini", "Ordini"], ["indirizzi", "Indirizzi"], ["dati", "Dati e privacy"]].map(([k, l]) => (
              <button key={k} data-testid={`account-section-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border ${tab === k ? "bg-[#39251D] text-[#F5F0E8] border-[#39251D]" : "border-[#D5B98C]"}`}>{l}</button>
            ))}
            <button data-testid="account-logout" onClick={logout} className="ml-auto text-xs underline">Esci</button>
          </div>
          {tab === "ordini" && <Orders />}
          {tab === "indirizzi" && <Addresses customer={customer} setCustomer={setCustomer} />}
          {tab === "dati" && <Profile customer={customer} setCustomer={setCustomer} onDeleted={() => { setCustomer(false); navigate("/"); }} />}
        </>
      )}
    </div>
  );
}

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => { e.preventDefault(); await api.post("/customer/forgot-password", { email }).catch(() => {}); setSent(true); };
  return (
    <form onSubmit={submit} className="max-w-md mx-auto px-5 pt-16 space-y-4" data-testid="forgot-page">
      <h1 className="font-serif text-4xl text-[#39251D]">Recupera password</h1>
      {sent ? <p data-testid="forgot-sent" className="text-sm">Se l'email è registrata riceverai un link per reimpostare la password (valido 1 ora).</p> : <>
        <input data-testid="forgot-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={shopInput} />
        <button data-testid="forgot-submit" className={`${shopBtn} w-full`}>Invia link</button></>}
    </form>
  );
};

export const ResetPassword = () => {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/customer/reset-password", { token: sp.get("token"), password: pwd }); toast.success("Password aggiornata: ora puoi accedere"); navigate("/account"); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <form onSubmit={submit} className="max-w-md mx-auto px-5 pt-16 space-y-4" data-testid="reset-page">
      <h1 className="font-serif text-4xl text-[#39251D]">Nuova password</h1>
      <input data-testid="reset-password" type="password" required minLength={8} placeholder="Nuova password (min. 8)" value={pwd} onChange={(e) => setPwd(e.target.value)} className={shopInput} />
      <button data-testid="reset-submit" className={`${shopBtn} w-full`}>Salva password</button>
    </form>
  );
};
